import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ZoomContext } from "../../zoom/zoom-context";

interface ScreenShareApi {
  /** True if the local user is currently sharing. */
  isSelfSharing: boolean;
  /** UserId of a remote participant whose share we're rendering, if any. */
  activeRemoteShareUserId?: number;
  /** Self-share goes into a <video> — the SDK requires this when WebCodecs
   *  is enabled (`startShareScreen` rejects a canvas in that mode). */
  selfVideoRef: React.RefObject<HTMLVideoElement>;
  /** Remote share is drawn onto a <canvas>. */
  remoteCanvasRef: React.RefObject<HTMLCanvasElement>;
  startShare: () => Promise<void>;
  stopShare: () => Promise<void>;
}

/**
 * Adds Zoom Video SDK screen-share capability.
 *   - Local share: we call `stream.startShareScreen(canvas)` with a canvas
 *     ref so the user can pick a window/tab and broadcast it.
 *   - Remote share: when `active-share-change` fires we mirror the remote's
 *     share into a separate canvas via `stream.startShareView`.
 *
 * Both canvases live in <ScreenShareView /> so this hook only owns the refs
 * and the side-effects.
 */
export function useScreenShare(): ScreenShareApi {
  const zmClient = useContext(ZoomContext);
  const selfVideoRef = useRef<HTMLVideoElement>(null);
  const remoteCanvasRef = useRef<HTMLCanvasElement>(null);

  const [isSelfSharing, setIsSelfSharing] = useState(false);
  const [activeRemoteShareUserId, setActiveRemoteShareUserId] = useState<
    number | undefined
  >();

  const startShare = useCallback(async () => {
    const stream: any = zmClient?.getMediaStream?.();
    const el = selfVideoRef.current;
    if (!stream || !el) return;
    try {
      await stream.startShareScreen(el);
      setIsSelfSharing(true);
    } catch (e) {
      console.warn("startShareScreen failed", e);
    }
  }, [zmClient]);

  const stopShare = useCallback(async () => {
    const stream: any = zmClient?.getMediaStream?.();
    if (!stream) return;
    try {
      await stream.stopShareScreen();
    } catch {
      /* ignore */
    }
    setIsSelfSharing(false);
  }, [zmClient]);

  // ----- remote share view ------------------------------------------------
  useEffect(() => {
    if (!zmClient) return;
    const onChange = (payload: any) => {
      const stream: any = zmClient?.getMediaStream?.();
      if (!stream) return;
      if (payload?.state === "Active") {
        const canvas = remoteCanvasRef.current;
        if (canvas) {
          stream
            .startShareView(canvas, payload.userId)
            .then(() => setActiveRemoteShareUserId(payload.userId))
            .catch((e: any) => console.warn("startShareView failed", e));
        }
      } else if (payload?.state === "Inactive") {
        stream
          .stopShareView()
          .catch(() => {})
          .finally(() => setActiveRemoteShareUserId(undefined));
      }
    };
    zmClient.on("active-share-change", onChange);
    return () => zmClient.off("active-share-change", onChange);
  }, [zmClient]);

  // ----- local share auto-stopped (e.g. user clicked "Stop sharing"
  //       in Chrome's tab picker) -----------------------------------------
  useEffect(() => {
    if (!zmClient) return;
    const onPassiveStop = () => setIsSelfSharing(false);
    zmClient.on("passively-stop-share", onPassiveStop);
    return () => zmClient.off("passively-stop-share", onPassiveStop);
  }, [zmClient]);

  return {
    isSelfSharing,
    activeRemoteShareUserId,
    selfVideoRef,
    remoteCanvasRef,
    startShare,
    stopShare,
  };
}
